import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Mail01Icon,
  AccessIcon,
  ArrowRight01Icon,
  SmartPhone01Icon,
  Tick01Icon,
  Cancel01Icon,
  FlashIcon,
  ViewIcon,
  ViewOffSlashIcon
} from '@hugeicons/core-free-icons';
import { FLAGS } from '../config/flags';
import { api } from '../utils/api';
import Button from '../components/Button';
import { useToast } from '../contexts/ToastContext';

const Signup = () => {
  const [role, setRole] = useState('client');
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState(0); // 0-4
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { addToast } = useToast();

  useEffect(() => {
    setPasswordsMatch(password === confirmPassword || confirmPassword === '');
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    setPasswordStrength(Math.min(score, 4));
  }, [password, confirmPassword]);

  const getStrengthColor = () => {
    if (password.length === 0) return 'bg-gray-200 dark:bg-gray-700';
    if (passwordStrength <= 1) return 'bg-red-500';
    if (passwordStrength === 2) return 'bg-yellow-500';
    if (passwordStrength === 3) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = () => {
    if (password.length === 0) return '';
    if (passwordStrength <= 1) return 'Weak';
    if (passwordStrength === 2) return 'Fair';
    if (passwordStrength === 3) return 'Good';
    return 'Strong';
  };

  const generateSecurePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const spec = "!@#$%^&*";
    let pass = "";
    pass += upper[Math.floor(Math.random() * upper.length)];
    pass += nums[Math.floor(Math.random() * nums.length)];
    pass += spec[Math.floor(Math.random() * spec.length)];
    const all = chars + upper + nums + spec;
    for (let i = 0; i < 9; i++) pass += all[Math.floor(Math.random() * all.length)];
    pass = pass.split('').sort(() => 0.5 - Math.random()).join('');
    setPassword(pass);
    setConfirmPassword(pass);
    addToast("Secure password generated!", "success");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        setLoading(false);
        return;
    }

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 800));
      navigate('/email-verification?email=' + encodeURIComponent(data.email));
      setLoading(false);
      return;
    }
    
    try {
      await api.post('/register', {
        email: data.email,
        phone_prefix: data.phone_prefix,
        phone_number: data.phone_number,
        password: password,
        password_confirmation: confirmPassword,
        role: role
      });

      navigate('/email-verification?email=' + encodeURIComponent(data.email));
    } catch (err) {
      if (err.errors) {
        const firstErrorField = Object.keys(err.errors)[0];
        setError(err.errors[firstErrorField][0]);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full space-y-8 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-3xl border border-light-gray/20 dark:border-dark-border/20 shadow-sm">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-charcoal dark:text-white">Create Account</h2>
          <p className="mt-2 text-muted-gray">Start trading securely on Safzi</p>
        </div>

        {/* Role Selector */}
        <div className="flex p-1 bg-off-white/50 dark:bg-gray-800/50 rounded-2xl border border-light-gray/20 dark:border-gray-700/50">
          <button
            onClick={() => setRole('client')}
            className={`flex-1 py-2 text-sm font-bold rounded-xl transition-all ${
              role === 'client' ? 'bg-white dark:bg-dark-surface text-soft-green dark:text-light-mint shadow-sm' : 'text-muted-gray'
            }`}
          >
            I'm a Client
          </button>
          <button
            onClick={() => setRole('agent')}
            className={`flex-1 py-2 text-sm font-bold rounded-xl transition-all ${
              role === 'agent' ? 'bg-white dark:bg-dark-surface text-soft-green dark:text-light-mint shadow-sm' : 'text-muted-gray'
            }`}
          >
            I'm an Agent
          </button>
        </div>
        
        <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
        
          {error && (
            <div className="text-red-500 text-sm text-center p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">Email Address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                <HugeiconsIcon icon={Mail01Icon} size={20} />
              </div>
              <input
                type="email"
                name="email"
                required
                className="block w-full pl-10 pr-3 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                placeholder="name@company.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">Phone Number</label>
            <div className="flex items-center gap-2">
              <div>
                <input
                  type="text"
                  name="phone_prefix"
                  defaultValue="+234"
                  readOnly
                  className="block w-24 px-3 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-0 text-muted-gray bg-off-white/80 dark:bg-gray-800/80 cursor-not-allowed text-center font-bold"
                />
              </div>
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                  <HugeiconsIcon icon={SmartPhone01Icon} size={20} />
                </div>
                <input
                  type="tel"
                  name="phone_number"
                  required
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '');
                  }}
                  className="block w-full pl-10 pr-3 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                  placeholder="700 000 000"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
             <div className="flex justify-between items-center px-1">
              <label className="text-sm font-medium text-charcoal dark:text-gray-200">Password</label>
              <button 
                type="button" 
                onClick={generateSecurePassword}
                className="text-[10px] font-black text-soft-green hover:text-light-mint transition-colors uppercase tracking-widest flex items-center gap-1"
              >
                <HugeiconsIcon icon={FlashIcon} size={10} /> Generate
              </button>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                <HugeiconsIcon icon={AccessIcon} size={20} />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 text-muted-gray hover:text-charcoal dark:hover:text-white flex items-center justify-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                <HugeiconsIcon icon={showPassword ? ViewOffSlashIcon : ViewIcon} size={18} />
              </button>
            </div>
            
            <div className="space-y-1 px-1">
              <div className="flex items-center justify-between">
                <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mr-4">
                  <div className={`h-full transition-all duration-500 ${getStrengthColor()}`} style={{ width: `${(passwordStrength / 4) * 100}%` }}></div>
                </div>
                <span className="text-xs font-bold text-muted-gray min-w-[40px] text-right">{getStrengthLabel()}</span>
              </div>
              <div className={`flex items-center text-[10px] font-bold ${password.length >= 8 ? 'text-green-500' : 'text-muted-gray'}`}>
                <HugeiconsIcon icon={password.length >= 8 ? Tick01Icon : Cancel01Icon} size={10} className="mr-1.5" />
                Min 8 Characters
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-charcoal dark:text-gray-200 px-1">Confirm Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                <HugeiconsIcon icon={AccessIcon} size={20} />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password_confirmation"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className={`block w-full pl-10 pr-10 py-3 border rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50 transition-colors ${
                    passwordsMatch ? 'border-light-gray/20 dark:border-gray-700/50' : 'border-red-500 focus:border-red-500 focus:ring-red-500'
                }`}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 text-muted-gray hover:text-charcoal dark:hover:text-white flex items-center justify-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                <HugeiconsIcon icon={showPassword ? ViewOffSlashIcon : ViewIcon} size={18} />
              </button>
            </div>
            {!passwordsMatch && confirmPassword !== '' && (
              <p className="text-xs text-red-500 flex items-center px-1">
                <HugeiconsIcon icon={Cancel01Icon} size={12} className="mr-1.5" />
                Passwords do not match
              </p>
            )}
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={loading || !passwordsMatch || password.length < 8}
              className="w-full relative"
            >
              {loading ? 'Creating Account...' : `Create ${role === 'agent' ? 'Agent' : 'Client'} Account`}
              <span className="absolute right-4 flex items-center">
                <HugeiconsIcon icon={ArrowRight01Icon} size={18} />
              </span>
            </Button>
          </div>
        </form>

        <div className="text-center">
          <p className="text-sm text-muted-gray">
            Already have an account?{' '}
            <Link to="/login" className="font-bold text-soft-green hover:text-light-mint">
              Sign In
            </Link>
          </p>
        </div>

        <p className="text-xs text-center text-muted-gray">
          By creating an account, you agree to Safzi's{' '}
          <Link to="/terms-of-service" className="font-bold text-soft-green hover:text-light-mint">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy-policy" className="font-bold text-soft-green hover:text-light-mint">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default Signup;
